import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { ACCENT } from "@/lib/siteTheme";
import logo from "@/assets/design/blue-heron-logo.webp";
import googleBadge from "@/assets/design/google-icon.webp";
import MobileNav from "./MobileNav";

export type ActiveNav = "home" | "services" | "reviews" | "plans" | "case-studies" | "about" | "contact" | null;

const caseStudyLinks = [
  { to: "/case-studies#seo", label: "Local SEO" },
  { to: "/case-studies#ads", label: "Google Ads" },
  { to: "/case-studies#websites", label: "Websites" },
];

const navLinkClass = "text-white/80 hover:text-[#E8A33D] no-underline font-semibold text-[15px] tracking-[0.01em] transition-colors";
const activeLinkStyle = { color: ACCENT, fontWeight: 700 };

const SiteHeader = ({ active = null }: { active?: ActiveNav }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      id="bhHeader"
      className="sticky top-0 z-50 px-6 md:px-14 py-[18px] -mb-[76px] border-b transition-[background-color,border-color] duration-300"
      style={{
        background: scrolled ? "rgba(8,20,35,0.92)" : "transparent",
        borderBottomColor: scrolled ? "rgba(244,241,234,0.08)" : "transparent",
        backdropFilter: scrolled ? "blur(10px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(10px)" : "none",
      }}
    >
      <div className="max-w-[1280px] mx-auto flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center">
            <img src={logo} alt="Blue Heron" width={448} height={80} className="h-[30px] w-auto block brightness-0 invert" />
          </Link>
          <a
            href="https://maps.app.goo.gl/q3Hk61Px8k53Yxwi9"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:inline-flex items-center gap-[7px] bg-white rounded-full py-1.5 pl-2.5 pr-3.5 text-[#081627] font-extrabold text-[11px] tracking-[0.05em] uppercase whitespace-nowrap no-underline transition-all duration-300"
            style={{
              opacity: scrolled ? 1 : 0,
              transform: scrolled ? "none" : "translateX(-8px)",
              pointerEvents: scrolled ? "auto" : "none",
            }}
          >
            <img src={googleBadge} alt="" width={94} height={96} className="w-3.5 h-3.5 block" />
            5 Star Rated on Google
          </a>
        </div>

        <nav className="hidden md:flex items-center gap-9">
          <Link to="/#services" className={navLinkClass} style={active === "services" ? activeLinkStyle : undefined}>
            Services
          </Link>
          <Link to="/reviews" className={navLinkClass} style={active === "reviews" ? activeLinkStyle : undefined}>
            Reviews
          </Link>
          <Link to="/plans" className={navLinkClass} style={active === "plans" ? activeLinkStyle : undefined}>
            Plans
          </Link>
          <div className="relative flex items-center group/cs">
            <Link
              to="/case-studies"
              className={`${navLinkClass} inline-flex items-center gap-1`}
              style={active === "case-studies" ? activeLinkStyle : undefined}
            >
              Case Studies <ChevronDown className="w-3 h-3 opacity-70 transition-transform group-hover/cs:rotate-180" />
            </Link>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3.5 w-[200px] invisible opacity-0 translate-y-1.5 group-hover/cs:visible group-hover/cs:opacity-100 group-hover/cs:translate-y-0 transition-all duration-200">
              <div className="bg-[#0C1E33] border border-white/10 rounded-2xl p-1.5 shadow-2xl flex flex-col gap-0.5">
                {caseStudyLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block px-3.5 py-2.5 rounded-lg text-white/85 hover:bg-white/10 hover:text-[#F7F3EC] no-underline font-semibold text-sm whitespace-nowrap transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <Link to="/about" className={navLinkClass} style={active === "about" ? activeLinkStyle : undefined}>
            About
          </Link>
          <Link to="/contact" className={navLinkClass} style={active === "contact" ? activeLinkStyle : undefined}>
            Contact
          </Link>
          <Link
            to="/book"
            className="inline-flex items-center font-extrabold text-sm px-6 py-3 rounded-full no-underline transition-transform hover:-translate-y-0.5"
            style={{ background: ACCENT, color: "#081627" }}
          >
            Book a Call
          </Link>
        </nav>

        <MobileNav active={active} />
      </div>
    </header>
  );
};

export default SiteHeader;
